import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  Search,
} from "@/components/ui";
import { useSnomedSearch } from "@/network/useOntologies";
import { getDisplay } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitCancelButtons } from "./SubmitCancelButtons";
import type {
  GenericObservationDisplayProps,
  GenericObservationFormProps,
} from "./types";

const formSchema = z.object({
  valueCode: z.string().min(1, "Code is required"),
  valueCodeDisplay: z.string().min(1, "Display is required"),
  valueCodeSystem: z.string().min(1, "System is required"),
});

export const GenericObservationCodeForm = ({
  patientId,
  item,
  itemCategory,
  onSubmit,
  onCancel,
  isSubmitting,
}: GenericObservationFormProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const eclExpression = item.parentCodes
    ? `<< (${item.parentCodes.join(" OR ")})`
    : `<< ${item.parentCode}`;
  const { data: codes, isLoading: isLoadingCodes } = useSnomedSearch({
    enabled: Boolean(item.parentCode || item.parentCodes),
    preloadOptions: item.preloadOptions,
    searchTerm,
    eclExpression,
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    // HACK: note that in this case, we are ok hardcoding the 0th index as
    // the code systems for generic observations are very unlikely to change
    defaultValues: {
      valueCode:
        item.documentedObservation?.valueCodeableConcept?.coding?.[0].code,
      valueCodeDisplay: item.documentedObservation?.valueCodeableConcept
        ? getDisplay(item.documentedObservation?.valueCodeableConcept)
        : undefined,
      valueCodeSystem:
        item.documentedObservation?.valueCodeableConcept?.coding?.[0].system,
    },
  });
  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    const fhirValue = {
      valueCodeableConcept: {
        coding: [
          {
            code: data.valueCode,
            system: data.valueCodeSystem,
            display: data.valueCodeDisplay,
          },
        ],
        text: data.valueCodeDisplay,
      },
    };
    const payload = {
      patientId: patientId as string,
      category: itemCategory,
      status: "final",
      code: {
        coding: [
          {
            code: item.observationCode,
            system: item.observationCodeSystem,
            display: item.observationCodeDisplay,
          },
        ],
        text: item.observationCodeFriendlyDisplay,
      },
      fhirValue,
    };
    onSubmit(payload);
  };
  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2">
        <Controller
          control={form.control}
          name="valueCode"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="valueCode">
                <BodyText size="sm" bold>
                  {item.observationCodeFriendlyDisplay}
                </BodyText>
              </FieldLabel>
              <Search
                {...field}
                autoFocusInput
                placeholder="Search"
                isLoading={
                  item.parentCode || item.parentCodes ? isLoadingCodes : false
                }
                input={searchTerm}
                onInputChange={(value) => setSearchTerm(value)}
                options={
                  (item.parentCode || item.parentCodes
                    ? codes?.map((code) => ({
                        label: code.display,
                        value: code.code,
                        metadata: {
                          system: code.system,
                          friendlyDisplay: code.display,
                        },
                      }))
                    : item.observationValues?.map((code) => ({
                        label: code.display,
                        value: code.value,
                        metadata: {
                          system: code.system,
                          friendlyDisplay: code.friendlyDisplay,
                        },
                      }))) || []
                }
                onItemSelected={(item) => {
                  form.setValue("valueCode", item.value as string);
                  form.setValue("valueCodeDisplay", item.label);
                  form.setValue(
                    "valueCodeSystem",
                    (item.metadata as { system?: string })?.system || "",
                  );
                }}
                openWithoutInput={true}
                shouldFilter={!(item.parentCode || item.parentCodes)}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </div>
    </form>
  );
};

export const GenericObservationCodeDisplay = ({
  item,
}: GenericObservationDisplayProps) => {
  return (
    <BodyText size="sm">
      {item.documentedObservation?.valueCodeableConcept
        ? getDisplay(item.documentedObservation.valueCodeableConcept)
        : "Unknown"}
    </BodyText>
  );
};
